/**
 * Exponential backoff helper for sequential polling.
 * - Waits for request completion/timeout before scheduling the next.
 * - On success, resets to the base interval.
 * - On error, increases delay up to a max with optional jitter.
 */

/**
 * Sleep helper.
 * @param {number} ms
 * @returns {Promise<void>}
 */
export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Create an exponential backoff controller.
 *
 * @param {Object} options
 * @param {number} options.initialMs - Initial/base delay (also used after success)
 * @param {number} options.maxMs - Maximum backoff delay
 * @param {number} options.factor - Multiplicative factor per error step
 * @param {boolean} options.jitter - Apply +/-20% jitter to reduce thundering herd
 */
export const createExponentialBackoff = ({
  initialMs = 3000,
  maxMs = 180000, // 3 minutes
  factor = 2,
  jitter = true,
} = {}) => {
  let currentDelay = initialMs;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  return {
    /** Reset backoff to the initial delay (use after successful requests). */
    reset() {
      currentDelay = initialMs;
    },

    /**
     * Get next delay after an error and advance the internal state.
     * @returns {number} delay in ms
     */
    nextErrorDelay() {
      const base = currentDelay;
      // Grow for next time
      currentDelay = clamp(Math.floor(currentDelay * factor), initialMs, maxMs);
      if (!jitter) return base;
      const variance = Math.floor(base * 0.2); // +/-20%
      const delta = Math.floor(Math.random() * (2 * variance + 1)) - variance;
      return clamp(base + delta, 1000, maxMs);
    },

    /**
     * Get the configured base interval (use between successful polls).
     */
    baseInterval() {
      return initialMs;
    },

    /**
     * Inspect the current internal delay (for debugging/telemetry).
     */
    getCurrentDelay() {
      return currentDelay;
    },
  };
};



